import type { ReactNode } from 'react'

import { Box, Hint, Text, theme } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import i18n from 'modules/core/lib/i18n'

import './index.styles.css'
import { useSignGoogleUrl } from './useSignGoogleUrl'

type Props = {
  lat: number
  lng: number
  editLocationButton: ReactNode
  radius: number
  showPopover: boolean
  onPopupClose: () => void
}

const drawCircle = (lat: number, lng: number, radius: number, numPoints: number): string => {
  const d2r = Math.PI / 180
  const r2d = 180 / Math.PI
  const earthsradius = 6378137

  const points: string[] = []
  const rlat = (radius / earthsradius) * r2d
  const rlng = rlat / Math.cos(lat * d2r)

  for (let i = 0; i < numPoints; i++) {
    const theta = Math.PI * (i / (numPoints / 2))
    const ex = lng + rlng * Math.cos(theta)
    const ey = lat + rlat * Math.sin(theta)
    points.push(`${ey},${ex}`)
  }

  return points.join('|')
}

const StaticMap = ({
  lat,
  lng,
  editLocationButton,
  radius,
  showPopover = false,
  onPopupClose,
}: Props) => {
  const circlePoints = drawCircle(lat, lng, radius, 100)
  const zoom = radius < 500 ? 17 : 14

  const rawUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&markers=size:big%7Ccolor:red%7C${lat},${lng}&path=color:0x00000000%7Cweight:0%7Cfillcolor:0x96B2FF56%7C${circlePoints}&zoom=${zoom}&size=360x360&maptype=roadmap`
  const { signedUrl, loading } = useSignGoogleUrl(rawUrl)

  if (loading) {
    return <Loading />
  }

  return (
    <Box position="relative" flexDirection="column" width="full" gap="s8">
      <Text.Normal size="200" weight="medium" color="grey1000">
        {i18n.t('settings.locations.map.label')}
      </Text.Normal>
      <Box
        background="grey000White"
        position="absolute"
        style={{
          top: theme.dimensions.s32,
          right: '0',
        }}
        borderRadius={{ all: 'abs016' }}
      >
        {editLocationButton}
      </Box>
      <img src={signedUrl} />
      {showPopover && (
        <Box className={'StaticMap_mapPopover'}>
          <Hint.Popover.Provider steps={1} isHidden={!showPopover} onFinish={onPopupClose}>
            <Hint.Popover
              step={1}
              title={i18n.t('settings.locations.map.popover.title')}
              description={i18n.t('settings.locations.map.popover.description')}
              actionLabel={i18n.t('common.close')}
              position="bottom left"
            />
          </Hint.Popover.Provider>
        </Box>
      )}
    </Box>
  )
}

export default observer(StaticMap)
